import React, { useEffect, useRef, useState } from "react";
import { BASE } from "../services/api";
import "../assets/css/FichaVisitaCampo.css";

const FORMATOS = ".pdf,.jpg,.jpeg,.png";
const EXTENSOES = /\.(pdf|jpe?g|png)$/i;

const nomeDoCaminho = (caminho) => String(caminho || "").split(/[\\/]/).pop();
const ehImagem = (nome) => /\.(jpe?g|png)$/i.test(String(nome || ""));

function tamanho(bytes) {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
}

function IconeArquivo({ imagem }) {
  return (
    <svg className="fvc-icone-svg" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 2h8l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z" fill="currentColor" opacity=".14" />
      <path d="M14 2v5h5M6 2h8l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"
        fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      {imagem ? (
        <path d="M7.5 17.5l3-3.5 2.2 2.4 1.6-1.8 2.2 2.9z" fill="currentColor" />
      ) : (
        <path d="M8 13h8M8 16.5h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      )}
    </svg>
  );
}

/** Ficha anexada a uma visita, com a troca do arquivo para gerente, diretor e administrativo.
 *
 *  A troca sobe o arquivo na hora (rota própria), sem esperar o "Salvar" do formulário, e
 *  devolve anexo + link novos por `onTrocada`: o formulário PRECISA guardar os dois, senão o
 *  PUT do salvar regrava o link antigo por cima. O servidor recusa a troca para outros perfis.
 */
export default function FichaVisitaCampo({ idVisita, anexo, link, podeTrocar, className = "", onTrocada }) {
  const [arquivo, setArquivo] = useState(null);
  const [previa, setPrevia] = useState("");
  const [arrastando, setArrastando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [aviso, setAviso] = useState(null);
  const entrada = useRef(null);
  const temLink = /^https?:\/\//i.test(String(link || ""));
  const nomeAtual = nomeDoCaminho(anexo);
  const temFicha = temLink || !!nomeAtual;

  // Prévia só para imagem; o objectURL é liberado ao trocar de arquivo ou desmontar.
  useEffect(() => {
    if (!arquivo || !ehImagem(arquivo.name)) { setPrevia(""); return undefined; }
    const url = URL.createObjectURL?.(arquivo) || "";
    setPrevia(url);
    return () => { if (url) URL.revokeObjectURL?.(url); };
  }, [arquivo]);

  const escolher = (f) => {
    setAviso(null);
    if (!f) return;
    if (!EXTENSOES.test(f.name || "")) {
      setArquivo(null);
      setAviso({ tipo: "erro", texto: "Formato inválido. Envie PDF ou imagem (JPG/PNG)." });
      return;
    }
    setArquivo(f);
  };

  const limpar = () => {
    setArquivo(null);
    if (entrada.current) entrada.current.value = "";
  };

  const trocar = async () => {
    if (!arquivo || enviando) return;
    setEnviando(true);
    setAviso(null);
    try {
      const fd = new FormData();
      fd.append("file", arquivo);
      const r = await fetch(`${BASE}/visitas/${encodeURIComponent(idVisita)}/ficha`, { method: "POST", body: fd });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.ok === false) throw new Error(d.error || d.message || "Não consegui trocar a ficha.");
      onTrocada?.({ anexoFichaVisita: d.anexoFichaVisita || "", linkImagem: d.linkImagem || "" });
      limpar();
      setAviso({ tipo: "ok", texto: "Ficha trocada. O arquivo anterior continua guardado no Drive." });
    } catch (e) {
      setAviso({ tipo: "erro", texto: e.message });
    } finally {
      setEnviando(false);
    }
  };

  const soltar = (e) => {
    e.preventDefault();
    setArrastando(false);
    if (!enviando) escolher(e.dataTransfer?.files?.[0]);
  };

  return (
    <section className={`fvc ${className}`} aria-label="Ficha da visita">
      <header className="fvc-topo">
        <span className="fvc-titulo">Ficha da visita</span>
        {!podeTrocar && (
          <span className="fvc-selo" title="Só gerente, diretor ou administrativo podem trocar a ficha.">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M7 10V8a5 5 0 0 1 10 0v2" fill="none" stroke="currentColor" strokeWidth="2" />
              <rect x="5" y="10" width="14" height="10" rx="2" fill="currentColor" />
            </svg>
            Somente leitura
          </span>
        )}
      </header>

      <div className={`fvc-atual ${temFicha ? "" : "fvc-atual--vazia"}`}>
        <span className="fvc-icone"><IconeArquivo imagem={ehImagem(nomeAtual)} /></span>
        <div className="fvc-info">
          <strong title={nomeAtual || undefined}>{temFicha ? (nomeAtual || "Ficha anexada") : "Nenhuma ficha anexada"}</strong>
          <small>{temFicha ? (temLink ? "Arquivo no Google Drive" : "Sem link para abrir") : "Esta visita ainda não tem comprovante"}</small>
        </div>
        {temLink && (
          <a className="fvc-abrir" href={link} target="_blank" rel="noopener noreferrer" aria-label="Abrir ficha atual">
            Abrir
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5"
                fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        )}
      </div>

      {podeTrocar ? (
        arquivo ? (
          <div className="fvc-novo">
            {previa ? <img className="fvc-previa" src={previa} alt="Prévia da nova ficha" />
              : <span className="fvc-icone fvc-icone--novo"><IconeArquivo imagem={ehImagem(arquivo.name)} /></span>}
            <div className="fvc-info">
              <strong title={arquivo.name}>{arquivo.name}</strong>
              <small>Nova ficha{arquivo.size ? ` · ${tamanho(arquivo.size)}` : ""} · substitui a atual</small>
            </div>
            <div className="fvc-acoes">
              <button type="button" className="fvc-botao fvc-botao--leve" onClick={limpar} disabled={enviando}>
                Cancelar
              </button>
              <button type="button" className="fvc-botao fvc-botao--principal" onClick={trocar} disabled={enviando}>
                {enviando && <span className="fvc-giro" aria-hidden="true" />}
                {enviando ? "Enviando…" : "Trocar ficha"}
              </button>
            </div>
          </div>
        ) : (
          <label
            className={`fvc-zona ${arrastando ? "fvc-zona--ativa" : ""}`}
            onDragOver={(e) => { e.preventDefault(); setArrastando(true); }}
            onDragLeave={() => setArrastando(false)}
            onDrop={soltar}
          >
            <input ref={entrada} type="file" accept={FORMATOS} aria-label="Arquivo da nova ficha"
              onChange={(e) => escolher(e.target.files?.[0] || null)} />
            <svg className="fvc-zona-icone" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 16V4M7 9l5-5 5 5M5 15v3a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-3"
                fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="fvc-zona-texto">
              <strong>{temFicha ? "Trocar a ficha" : "Anexar a ficha"}</strong> — arraste o arquivo aqui ou clique para escolher
            </span>
            <small>PDF, JPG ou PNG</small>
          </label>
        )
      ) : (
        <p className="fvc-dica">Só gerente, diretor ou administrativo podem trocar a ficha.</p>
      )}

      {aviso && (
        <p className={`fvc-aviso fvc-aviso--${aviso.tipo}`} role="status">
          {aviso.tipo === "ok" ? "✓ " : "! "}{aviso.texto}
        </p>
      )}
    </section>
  );
}
